import { effectivePostParagraphStyle } from './post_editor_paragraph_style.js';

/** Palette for horizontal rules (border color). */
export const POST_EDITOR_HR_COLORS = [
  '#000000',
  '#522A45',
  '#B8C5D6',
  '#D5AB32',
  '#9BA854',
  '#A8483D',
  '#F03E3E',
  '#A39382',
];

/** Font family class support and legacy mapping. */
export const POST_EDITOR_FONT_CLASS_VALUES = new Set([
  'font-primary',
  'font-primary-black',
  'font-primary-bold',
  'font-primary-medium',
  'font-primary-thin',
  'font-primary-light',
  'font-secondary',
  'font-secondary-italic',
]);

/** Paragraph text styles for the toolbar select (Body, Caption, Heading, Subheading). */
export const POST_EDITOR_PARAGRAPH_STYLE_OPTIONS = [
  { value: 'body', label: 'Body' },
  { value: 'caption', label: 'Caption' },
  { value: 'heading', label: 'Heading' },
  { value: 'subheading', label: 'Subheading' },
];

/** Font families for dropdown rendering (value: class name, label: display text). */
export const POST_EDITOR_FONT_FAMILIES = [
  { value: 'font-primary', label: 'Calibre' },
  { value: 'font-primary-bold', label: 'Calibre Bold' },
  { value: 'font-primary-black', label: 'Calibre Black' },
  { value: 'font-primary-medium', label: 'Calibre Medium' },
  { value: 'font-secondary', label: 'Filosofia OT' },
  { value: 'font-secondary-italic', label: 'Filosofia OT Italic' },
];

/**
 * StarterKit marks omitted from the post editor toolbar — set to false so
 * Cmd+B / Cmd+I and `**` / `*` input rules do not apply.
 */
export const POST_EDITOR_DISABLED_STARTER_KIT_MARKS = {
  bold: false,
  italic: false,
};

/** Inline text colors for caption / non-body paragraphs (Black + Gray 06 only). */
export const POST_EDITOR_INLINE_TEXT_COLORS = Object.freeze([
  { id: 'ps-black', label: 'Black', hex: '#000000', className: 'ps-swatch-bg--black' },
  { id: 'ps-gray-06', label: 'Gray 06', hex: '#817569', className: 'ps-swatch-bg--ps-gray-06' },
]);

/** Maps hex color values to toolbar background swatch classes. */
export const POST_EDITOR_TOOLBAR_BG_CLASS_BY_HEX = {
  '#000000': 'ps-swatch-bg--black',
  '#B8C5D6': 'ps-swatch-bg--ps-light-blue',
  '#D5AB32': 'ps-swatch-bg--ps-gold',
  '#9BA854': 'ps-swatch-bg--ps-moss',
  '#A8483D': 'ps-swatch-bg--ps-brick',
  '#F03E3E': 'ps-swatch-bg--ps-red',
  '#A39382': 'ps-swatch-bg--ps-gray',
  '#61584F': 'ps-swatch-bg--ps-gray-07',
  '#817569': 'ps-swatch-bg--ps-gray-06',
  '#522A45': 'ps-swatch-bg--ps-plum',
  '#107FAE': 'ps-swatch-bg--ps-bright-blue-06',
  '#75556A': 'ps-swatch-bg--ps-plum-04',
  '#FFFFFF': 'ps-swatch-bg--white',
};

/** Maps hex color values to toolbar text color classes. */
export const POST_EDITOR_TOOLBAR_TEXT_CLASS_BY_HEX = {
  '#000000': 'ps-text-color--ps-black',
  '#9BA854': 'ps-text-color--ps-moss',
  '#A8483D': 'ps-text-color--ps-brick',
  '#F03E3E': 'ps-text-color--ps-red',
  '#61584F': 'ps-text-color--ps-gray-07',
  '#817569': 'ps-text-color--ps-gray-06',
  '#522A45': 'ps-text-color--ps-plum',
  '#107FAE': 'ps-text-color--ps-bright-blue-06',
  '#75556A': 'ps-text-color--ps-plum-04',
};

/** Paragraph text style classes (body, caption, heading, subheading). */
export const POST_EDITOR_PARAGRAPH_STYLE_CLASSES = {
  body: 'ps-post-style-body',
  caption: 'ps-post-style-caption',
  heading: 'ps-post-style-heading',
  subheading: 'ps-post-style-subheading',
};

/** Valid accent IDs for heading and subheading styles. */
export const POST_EDITOR_PARAGRAPH_ACCENT_IDS = ['black', 'gray-07', 'plum', 'red', 'gold', 'moss'];

/** Maps accent IDs to their corresponding CSS classes. */
export const POST_EDITOR_PARAGRAPH_ACCENT_CLASSES = {
  black: 'ps-heading-black',
  'gray-07': 'ps-heading-gray-07',
  plum: 'ps-heading-plum',
  red: 'ps-heading-red',
  gold: 'ps-heading-gold',
  moss: 'ps-heading-moss',
};

/** Maps paragraph text styles to semantic HTML tags. */
export const POST_EDITOR_PARAGRAPH_TAG_BY_STYLE = {
  heading: 'h2',
  subheading: 'h3',
};

/** Block types that support indentation. */
export const POST_EDITOR_BLOCK_INDENT_TYPES = [
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'resourceTable',
  'horizontalRule',
  'blockquote',
  'image',
  'video',
  'embedVideo',
  'postImageCarousel',
];

/** Maximum indent level (visual indent via margin-left or list nesting). */
export const POST_EDITOR_MAX_INDENT = 8;

/** Minimum indent level. */
export const POST_EDITOR_MIN_INDENT = 0;

/** Legacy text color tokens (ps-black, ps-white, ps-gold, ps-brick, ps-moss). */
export const POST_EDITOR_TEXT_COLOR_LEGACY_TOKENS = new Set(['black', 'white', 'gold', 'brick', 'moss']);

/** Spacer sizes (in pixels): s=12px, m=20px, l=24px, xl=32px, xxl=48px. */
export const POST_EDITOR_SPACER_SIZES = Object.freeze({
  s: 12,
  m: 20,
  l: 24,
  xl: 32,
  xxl: 48,
});

/** Valid spacer size keys. */
export const POST_EDITOR_SPACER_SIZE_KEYS = Object.freeze(['s', 'm', 'l', 'xl', 'xxl']);

/** Default spacer size. */
export const POST_EDITOR_SPACER_DEFAULT_SIZE = 'm';

/** Labels for spacer sizes (UI display). */
export const POST_EDITOR_SPACER_SIZE_LABELS = Object.freeze({
  s: 'S',
  m: 'M',
  l: 'L',
  xl: 'XL',
  xxl: 'XXL',
});

/** Divider color classes from `dividers.scss` (`.hr` + `.hr-*`). */
const HR_DIVIDER_COLOR_CLASS_BY_HEX = {
  '#522A45': 'hr-plum',
  '#B8C5D6': 'hr-lb',
  '#D5AB32': 'hr-gold',
  '#9BA854': 'hr-moss',
  '#A8483D': 'hr-brick',
  '#F03E3E': 'hr-red',
  '#A39382': 'hr-gray',
};

const HR_DIVIDER_CLASS_TO_HEX = Object.fromEntries(
  Object.entries(HR_DIVIDER_COLOR_CLASS_BY_HEX).map(([hex, cls]) => [cls, hex]),
);

/** HR border width in px (stored as string keys). */
export const POST_EDITOR_HR_WIDTHS = ['1', '5'];

/** HR width options for toolbar / drag-menu UI. */
export const POST_EDITOR_HR_WIDTH_OPTIONS = Object.freeze([
  { width: '1', label: '1px' },
  { width: '5', label: '5px' },
]);

/** Human-readable label for an HR palette hex (toolbar + drag menu). */
export function postEditorHrColorLabel(hex) {
  const h = (hex || '').toString().toUpperCase();
  switch (h) {
    case '#000000':
      return 'Black';
    case '#522A45':
      return 'Plum';
    case '#B8C5D6':
      return 'Light blue';
    case '#D5AB32':
      return 'Gold';
    case '#9BA854':
      return 'Moss';
    case '#A8483D':
      return 'Brick';
    case '#F03E3E':
      return 'Red';
    case '#A39382':
      return 'Gray';
    default:
      return h || 'Color';
  }
}

/** One marker color per ordered list (entire list). */
export const POST_EDITOR_ORDERED_MARKER_COLORS = [
  '#000000',
  '#9BA854',
  '#A8483D',
  '#F03E3E',
  '#61584F',
  '#522A45',
];

/** Maps normalized ordered-list marker hex → `ps-ol--*` modifier. */
export const POST_EDITOR_ORDERED_MARKER_SLUG_BY_HEX = {
  '#000000': 'c-black',
  '#9BA854': 'c-moss',
  '#A8483D': 'c-brick',
  '#F03E3E': 'c-red',
  '#61584F': 'c-gray-07',
  '#522A45': 'c-plum',
};

const ORDERED_SLUG_TO_HEX = Object.fromEntries(
  Object.entries(POST_EDITOR_ORDERED_MARKER_SLUG_BY_HEX).map(([hex, slug]) => [slug, hex]),
);

/** Helper to extract font class from a className string. */
export function findFontClass(className) {
  if (!className) return null;
  return className.split(/\s+/).find(cls => POST_EDITOR_FONT_CLASS_VALUES.has(cls)) || null;
}

export function normalizeHrColor(value) {
  const v = (value || '').toString().toUpperCase();
  const found = POST_EDITOR_HR_COLORS.find((c) => c.toUpperCase() === v);
  return found || '#000000';
}

export function normalizeHrWidth(value) {
  const w = String(value || '1');
  return POST_EDITOR_HR_WIDTHS.includes(w) ? w : '1';
}

export function normalizeOrderedMarkerColor(value) {
  const v = (value || '').toString().toUpperCase();
  const found = POST_EDITOR_ORDERED_MARKER_COLORS.find((c) => c.toUpperCase() === v);
  return found || '#000000';
}

/** Space-separated classes for `<hr>` (uses `dividers.scss`: `.hr`, `.hr-lg`, `.hr-*`). */
export function postEditorHrClassList(color, lineWidth) {
  const c = normalizeHrColor(color);
  const w = normalizeHrWidth(lineWidth);
  const parts = ['hr'];
  if (w === '5') parts.push('hr-lg');
  const colorClass = HR_DIVIDER_COLOR_CLASS_BY_HEX[c];
  if (colorClass) parts.push(colorClass);
  return parts.join(' ');
}

/** Single color modifier class for `<ol>` (plus `ps-post-ordered-list` from the same attribute render). */
export function postEditorOrderedListMarkerClass(color) {
  const c = normalizeOrderedMarkerColor(color);
  const slug = POST_EDITOR_ORDERED_MARKER_SLUG_BY_HEX[c] || 'c-black';
  return `ps-ol--${slug}`;
}

export function parseHrAttrsFromClassName(className) {
  const str = (className || '').toString();
  const tokens = str.trim().split(/\s+/).filter(Boolean);
  const hasDividerBase = tokens.includes('hr');

  let lineWidth;
  if (tokens.includes('hr-lg')) lineWidth = '5';
  else if (hasDividerBase) lineWidth = '1';

  let color;
  for (const [cls, hex] of Object.entries(HR_DIVIDER_CLASS_TO_HEX)) {
    if (tokens.includes(cls)) {
      color = hex;
      break;
    }
  }
  if (!color && hasDividerBase) color = '#000000';

  return { lineWidth, color };
}

/** Pathstone pull-quote variants (fixed palette; not editable via text color). */
export const POST_EDITOR_QUOTE_VARIANTS = ['blue', 'plum', 'black', 'brick'];

/** Swatch metadata for toolbar (hex matches design / variables). */
export const POST_EDITOR_QUOTE_SWATCHES = [
  { id: 'blue', hex: '#107FAE' },
  { id: 'plum', hex: '#75556A' },
  { id: 'black', hex: '#000000' },
  { id: 'brick', hex: '#A8483D' },
];

export function normalizePathstoneQuoteVariant(v) {
  if (POST_EDITOR_QUOTE_VARIANTS.includes(v)) return v;
  return 'black';
}

/** True when a Pathstone blockquote has an explicit attribution paragraph. */
export function pathstoneQuoteHasAttribution(blockquoteNode) {
  if (!blockquoteNode) return false;
  let found = false;
  blockquoteNode.forEach((child) => {
    if (child.attrs?.psQuoteRole === 'attribution') found = true;
  });
  return found;
}

/** BEM kind slugs for admin floating bubble menus (use in CSS as `--{kind}`). */
export const POST_EDITOR_FLOATING_MENU_KINDS = Object.freeze({
  blockquote: 'blockquote',
  image: 'image',
  video: 'video',
  downloadRow: 'download-row',
  externalLinkRow: 'external-link-row',
  meetingRow: 'meeting-row',
  audioRow: 'audio-row',
});

const ROW_NODE_TYPE_TO_FLOATING_KIND = Object.freeze({
  downloadRow: POST_EDITOR_FLOATING_MENU_KINDS.downloadRow,
  externalLinkRow: POST_EDITOR_FLOATING_MENU_KINDS.externalLinkRow,
  meetingRow: POST_EDITOR_FLOATING_MENU_KINDS.meetingRow,
  audioRow: POST_EDITOR_FLOATING_MENU_KINDS.audioRow,
});

/** Map a resource row TipTap node type to a floating-menu kind slug. */
export function postEditorFloatingKindFromRowType(rowType) {
  if (!rowType) return null;
  return ROW_NODE_TYPE_TO_FLOATING_KIND[rowType] || null;
}

/** Container classes for floating-ui bubble menus in the admin editor. */
export function postEditorFloatingContainerClasses(kind, { popoverOpen = false, extra = [] } = {}) {
  return [
    'tiptap-node-floating-container',
    kind ? `tiptap-node-floating-container--${kind}` : null,
    popoverOpen ? 'tiptap-node-floating-container--popover-open' : null,
    ...extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/** Toolbar classes + data attributes for floating bubble menus. */
export function postEditorFloatingToolbarProps(kind, { ariaLabel = 'toolbar' } = {}) {
  return {
    role: 'toolbar',
    'aria-label': ariaLabel,
    'data-variant': 'floating',
    ...(kind ? { 'data-floating-kind': kind } : {}),
    className: ['tiptap-toolbar', kind ? `tiptap-toolbar--${kind}` : null].filter(Boolean).join(' '),
  };
}

/** Classes for the fixed video overlay menu (inside .video-extension-container). */
export function postEditorVideoFixedMenuClasses(extra = []) {
  const kind = POST_EDITOR_FLOATING_MENU_KINDS.video;
  return [
    'tiptap-video-fixed-menu',
    `tiptap-video-fixed-menu--${kind}`,
    ...extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/** Apply floating-toolbar data attributes/classes to a DOM element (node views). */
export function postEditorApplyFloatingToolbarElement(element, kind, { ariaLabel = 'toolbar' } = {}) {
  if (!element) return;
  const props = postEditorFloatingToolbarProps(kind, { ariaLabel });
  element.className = props.className;
  element.setAttribute('role', props.role);
  element.setAttribute('aria-label', props['aria-label']);
  element.setAttribute('data-variant', props['data-variant']);
  if (props['data-floating-kind']) {
    element.setAttribute('data-floating-kind', props['data-floating-kind']);
  }
}

/** Toolbar group class list for a floating-menu kind. */
export function postEditorFloatingToolbarGroupClasses(kind) {
  return ['tiptap-toolbar-group', kind ? `tiptap-toolbar-group--${kind}` : null].filter(Boolean).join(' ');
}

/** Only floating-ui position/transform — static layout lives in tiptap_floating_menu.scss. */
export function postEditorFloatingPositionStyle(floatingStyles) {
  return floatingStyles ?? undefined;
}

/** Placement for resource-row, image, and Pathstone blockquote bubble menus (top-right of reference). */
export const POST_EDITOR_BUBBLE_MENU_PLACEMENT = 'top-end';

/** Shared floating-ui offset (px) — keeps bubble menus above the anchor, not on top of text/media. */
export const POST_EDITOR_BUBBLE_MENU_OFFSET = 10;

/** Shared transform nudge after floating-ui positions the menu (images, rows, blockquotes). */
export const POST_EDITOR_BUBBLE_MENU_X_NUDGE = -59.5;
export const POST_EDITOR_BUBBLE_MENU_Y_NUDGE = 4;

/**
 * Nudge floating-ui `translate()` so bubble menus sit above row titles / quote lines / images.
 * @param {{ xNudge?: number, yNudge?: number }} [options]
 */
export function postEditorBubbleMenuFloatingStyles(floatingStyles, options = {}) {
  if (!floatingStyles?.transform) return floatingStyles;

  const transformMatch = floatingStyles.transform.match(/translate\(([\d.]+)px,\s*([\d.]+)px\)/);
  if (!transformMatch) return floatingStyles;

  const {
    xNudge = POST_EDITOR_BUBBLE_MENU_X_NUDGE,
    yNudge = POST_EDITOR_BUBBLE_MENU_Y_NUDGE,
  } = options;
  const currentX = parseFloat(transformMatch[1]);
  const currentY = parseFloat(transformMatch[2]);

  return {
    ...floatingStyles,
    transform: `translate(${currentX + xNudge}px, ${currentY + yNudge}px)`,
  };
}

/** DOM anchor for a Pathstone blockquote bubble menu (quote paragraph, like row title cell). */
export function postEditorBlockquoteFloatingReference(blockquoteDom) {
  if (!(blockquoteDom instanceof HTMLElement)) return null;
  return (
    blockquoteDom.querySelector('p[data-ps-quote-role="quote"]') ||
    blockquoteDom.querySelector('p:first-of-type') ||
    blockquoteDom
  );
}

/** DOM anchor for an image bubble menu — the visible <img>, not the full-width wrapper. */
export function postEditorImageFloatingReference(mediaDom) {
  if (!(mediaDom instanceof HTMLElement)) return null;

  if (
    mediaDom instanceof HTMLImageElement &&
    (mediaDom.classList.contains('tiptap-image-img') ||
      mediaDom.classList.contains('post-content-image-img'))
  ) {
    return mediaDom;
  }

  const img =
    mediaDom.querySelector('img.tiptap-image-img') ||
    mediaDom.querySelector('img.post-content-image-img');
  if (img instanceof HTMLImageElement) return img;

  const content = mediaDom.classList.contains('tiptap-image-content')
    ? mediaDom
    : mediaDom.querySelector('.tiptap-image-content');
  if (content instanceof HTMLElement) return content;

  return mediaDom;
}

/** DOM anchor for a resource-row bubble menu (title cell). */
export function postEditorResourceRowFloatingReference(rowElement) {
  if (!(rowElement instanceof HTMLElement)) return null;
  return (
    rowElement.querySelector('td.download-title') ||
    rowElement.querySelector('td:last-child') ||
    rowElement.querySelector('td:nth-child(2)') ||
    rowElement
  );
}

export function parseOrderedMarkerFromClassList(classList) {
  const iterable = classList && typeof classList[Symbol.iterator] === 'function' ? classList : [];
  for (const name of iterable) {
    const m = String(name).match(/^ps-ol--(c-[a-z0-9-]+)$/);
    if (m && ORDERED_SLUG_TO_HEX[m[1]]) return ORDERED_SLUG_TO_HEX[m[1]];
  }
  return null;
}

/** Normalize spacer size key to valid value (s, m, l, xl). */
export function normalizePostSpacerSize(size) {
  if (typeof size !== 'string') return POST_EDITOR_SPACER_DEFAULT_SIZE;
  const k = size.trim().toLowerCase();
  return POST_EDITOR_SPACER_SIZE_KEYS.includes(k) ? k : POST_EDITOR_SPACER_DEFAULT_SIZE;
}

/** TipTap node types whose inline content is a media caption (image / video / embed). */
export const POST_EDITOR_MEDIA_CAPTION_NODE_TYPES = Object.freeze([
  'image',
  'video',
  'embedVideo',
]);

const POST_EDITOR_MEDIA_CAPTION_NODE_TYPE_SET = new Set(POST_EDITOR_MEDIA_CAPTION_NODE_TYPES);

/** Toolbar color-picker modes (limited palette vs full grid). */
export const POST_EDITOR_COLOR_PICKER_MODES = Object.freeze({
  disabled: 'disabled',
  /** Announcement editor: Pathstone black + red only. */
  announcement: 'announcement',
  quote: 'quote',
  heading: 'heading',
  subheading: 'subheading',
  orderedList: 'orderedList',
  /** Post editor caption / inline text — compact list (same UI as announcement). */
  full: 'full',
});

/** Text colors for the announcement admin editor. */
export const POST_EDITOR_ANNOUNCEMENT_TEXT_COLORS = Object.freeze([
  { id: 'ps-black', label: 'Black', hex: '#000000', className: 'ps-swatch-bg--black' },
  { id: 'ps-red', label: 'Red', hex: '#f03e3e', className: 'ps-swatch-bg--ps-red' },
]);

/** All toolbar text-color swatches (announcement + post inline). */
export function postEditorToolbarTextColorSwatches() {
  return [...POST_EDITOR_ANNOUNCEMENT_TEXT_COLORS, ...POST_EDITOR_INLINE_TEXT_COLORS];
}

/** Titles shown in the toolbar color menu for each mode. */
export const POST_EDITOR_COLOR_PICKER_TITLES = Object.freeze({
  textColor: 'Text color',
  quoteColor: 'Quote color',
  headingColor: 'Heading color',
  subheadingColor: 'Subheading color',
  listMarkerColor: 'List marker color',
});

/** Tooltips when the paragraph-style dropdown is locked. */
export const POST_EDITOR_PARAGRAPH_STYLE_LOCK_TOOLTIPS = Object.freeze({
  resourceTable: 'Text style is locked in resource rows',
  mediaCaption: 'Text style is locked in media captions',
  blockquote: 'Text style is locked inside pull quotes',
  list: 'Text style is locked in lists',
});

/** Tooltips when list controls are locked inside a pull quote. */
export const POST_EDITOR_BLOCKQUOTE_LIST_LOCK_TOOLTIP =
  'Lists are not available inside pull quotes';

/** Tooltips when list controls are locked on Heading / Subheading. */
export const POST_EDITOR_HEADING_LIST_LOCK_TOOLTIP =
  'Lists are not available for Heading or Subheading';

/** Tooltips when the inline link control is locked on Heading / Subheading. */
export const POST_EDITOR_HEADING_LINK_LOCK_TOOLTIP =
  'Links are not available for Heading or Subheading';

/** Tooltips when the inline link control is locked inside a pull quote. */
export const POST_EDITOR_BLOCKQUOTE_LINK_LOCK_TOOLTIP =
  'Links are not available inside pull quotes';

/** Tooltip when horizontal rules are locked inside a pull quote. */
export const POST_EDITOR_BLOCKQUOTE_HR_LOCK_TOOLTIP =
  'Horizontal rules are not available inside pull quotes';

/**
 * True when the active editor is a carousel caption overlay or the cursor is inside
 * image / video / embed caption content in the main post editor.
 */
export function isPostEditorMediaCaptionContext(editor, activeOverlay) {
  if (!editor) return false;
  if (activeOverlay?.activeOverlayEditor && editor === activeOverlay.activeOverlayEditor) {
    return true;
  }
  const { state } = editor;
  if (!state) return false;
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    if (POST_EDITOR_MEDIA_CAPTION_NODE_TYPE_SET.has($from.node(depth).type.name)) {
      return true;
    }
  }
  return false;
}

/**
 * Which palette the toolbar color control should show (and how color picks apply).
 * @param {object} toolbarState
 * @param {{ featuresPreset?: string }} [options]
 * @returns {{ mode: string, title: string }}
 */
export function resolvePostEditorColorPickerContext(toolbarState, options = {}) {
  const M = POST_EDITOR_COLOR_PICKER_MODES;
  const T = POST_EDITOR_COLOR_PICKER_TITLES;
  if (options.featuresPreset === 'announcements') {
    return { mode: M.announcement, title: T.textColor };
  }
  if (toolbarState.resourceTable) {
    return { mode: M.disabled, title: T.textColor };
  }
  if (toolbarState.blockquote) {
    return { mode: M.quote, title: T.quoteColor };
  }
  // List contexts must take precedence over paragraph style (list items are often Body by default).
  if (toolbarState.orderedList) {
    return { mode: M.orderedList, title: T.listMarkerColor };
  }
  if (toolbarState.bulletList) {
    return { mode: M.disabled, title: T.textColor };
  }
  // Media captions are caption-only editors (single paragraph) but often have no
  // postTextStyle attr set. Treat them like "caption" for text color purposes.
  if (toolbarState.mediaCaption) {
    return { mode: M.full, title: T.textColor };
  }

  const paragraphStyle = effectivePostParagraphStyle(toolbarState.postTextStyle);
  // Body text is always black; disable text color for default/unset paragraphs too.
  if (paragraphStyle === 'body') {
    return { mode: M.disabled, title: T.textColor };
  }
  if (paragraphStyle === 'heading') {
    return { mode: M.heading, title: T.headingColor };
  }
  if (paragraphStyle === 'subheading') {
    return { mode: M.subheading, title: T.subheadingColor };
  }
  return { mode: M.full, title: T.textColor };
}

/** True when Body/Heading/Subheading/Caption style cannot be changed from the toolbar. */
export function isPostEditorParagraphStyleMenuLocked(toolbarState) {
  return !!(
    toolbarState.blockquote ||
    toolbarState.resourceTable ||
    toolbarState.mediaCaption ||
    toolbarState.bulletList ||
    toolbarState.orderedList
  );
}

/** Tooltip for a locked inline link button. */
export function postEditorInlineLinkLockTooltip(toolbarState) {
  if (toolbarState.blockquote) return POST_EDITOR_BLOCKQUOTE_LINK_LOCK_TOOLTIP;
  return POST_EDITOR_HEADING_LINK_LOCK_TOOLTIP;
}

/** Tooltip for a locked paragraph-style menu button. */
export function postEditorParagraphStyleLockTooltip(toolbarState) {
  if (toolbarState.resourceTable) return POST_EDITOR_PARAGRAPH_STYLE_LOCK_TOOLTIPS.resourceTable;
  if (toolbarState.mediaCaption) return POST_EDITOR_PARAGRAPH_STYLE_LOCK_TOOLTIPS.mediaCaption;
  if (toolbarState.blockquote) return POST_EDITOR_PARAGRAPH_STYLE_LOCK_TOOLTIPS.blockquote;
  if (toolbarState.bulletList || toolbarState.orderedList) {
    return POST_EDITOR_PARAGRAPH_STYLE_LOCK_TOOLTIPS.list;
  }
  return null;
}
